import type { Device } from '@studio/models/device';
import { StatusEgressState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { MultiDeviceSelector, StudioSelector } from '../types.js';

export interface RTMPDestinationFromState {
  id: string;
  nickName: string;
  url: string;
  streamKey: string;
}

export interface RTMPDestinationStatus {
  id: string;
  nickName: string;
  url: string;
  streamKey: string;
  duration?: number | undefined;
  state?: StatusEgressState;
  error?: string | undefined;
}

export const StudioRTMPDestinationsSelector: StudioSelector<{
  destinations: Record<string, RTMPDestinationFromState>;
}> = {
  transform: (studio) => {
    const rtmpDestinations = studio.rtmpEgress?.destination ?? {};
    const transformedDestinations: Record<string, RTMPDestinationFromState> = {};

    for (const [destId, egressRTMPDestination] of Object.entries(rtmpDestinations)) {
      if (egressRTMPDestination) {
        const { url, streamKey, nickName } = egressRTMPDestination.destinationOptions ?? {};

        // Only include destinations that have valid configuration
        if (url && streamKey && nickName) {
          transformedDestinations[destId] = {
            id: destId,
            nickName,
            url,
            streamKey
          };
        }
      }
    }

    return {
      destinations: transformedDestinations
    };
  }
};

// This hook is effectively the same as the above, but for the status of the destinations. This is used to monitor the current status
// of the destinations, and to determine if they are active or not.
export const DeviceRTMPDestinationsStatusSelector: MultiDeviceSelector<{
  id: Device['id'];
  destinations: Record<string, RTMPDestinationStatus>;
}> = {
  transform: (device) => {
    const rtmpDestinations = device.status?.egress?.rtmpStatus?.destinations ?? {};
    const transformedDestinations: Record<string, RTMPDestinationStatus> = {};

    for (const [destId, egressRTMPDestinationStatus] of Object.entries(rtmpDestinations)) {
      if (egressRTMPDestinationStatus) {
        const { url, streamKey, nickName, duration, state, error } =
          egressRTMPDestinationStatus ?? {};

        // Only include destinations that have valid configuration
        if (url && streamKey && nickName) {
          transformedDestinations[destId] = {
            id: destId,
            nickName,
            url,
            streamKey,
            state: state ?? StatusEgressState.EGRESS_STATE_UNKNOWN,
            error,
            duration
          };
        }
      }
    }

    return {
      id: device.id,
      destinations: transformedDestinations
    };
  }
};

export type StudioRTMPDestinationsSelectorType = NonNullable<
  (typeof StudioRTMPDestinationsSelector)['direct']
>;
